import { useState, useEffect, useRef, useImperativeHandle, forwardRef, useCallback } from 'react'
import 'leaflet/dist/leaflet.css'
import 'leaflet-draw/dist/leaflet.draw.css'
import L, { FeatureGroup, LatLngTuple } from 'leaflet'
import 'leaflet-draw'
import styled from 'styled-components'

const MapContainer = styled.div`
  height: 100%;
  width: 100%;
  position: relative;
  background: #1a1a1a;
  overflow: hidden;
  touch-action: none;
  border-radius: 8px;

  .leaflet-container {
    height: 100%;
    width: 100%;
    background: #1a1a1a;
  }

  #synth-map {
    height: 100%;
    width: 100%;
    z-index: 1;
  }
`

// Add this style block after the MapContainer styled component
const StyledMapContainer = styled(MapContainer)`
  .leaflet-custom-edit-marker {
    box-shadow: 0 0 8px rgba(51, 136, 255, 0.5);
    border: 2px solid #3388ff;
    background: white;
    transition: all 0.2s ease;
  }

  .leaflet-editing-icon {
    border-radius: 50% !important;
    border: 2px solid #3388ff !important;
    background: white !important;
    box-shadow: 0 0 8px rgba(51, 136, 255, 0.5) !important;
    width: 12px !important;
    height: 12px !important;
    margin-left: -6px !important;
    margin-top: -6px !important;
    transition: all 0.2s ease !important;

    &:hover {
      transform: scale(1.2);
      box-shadow: 0 0 12px rgba(51, 136, 255, 0.7) !important;
    }
  }

  .leaflet-edit-move {
    border-radius: 50%;
    border: 2px solid #3388ff;
    background: white;
    box-shadow: 0 0 8px rgba(51, 136, 255, 0.5);
  }

  .leaflet-vertex-icon {
    border-radius: 50% !important;
  }
`

interface SynthSelectionMapProps {
  onBoundsChange: (
    bounds: { north: string; south: string; east: string; west: string } | null
  ) => void
}

export interface SynthSelectionMapHandle {
  clearBounds: () => void
  startDrawing: () => void
  focusBounds: () => void
  setBounds: (bounds: { north: string; south: string; east: string; west: string }) => void
}

const SynthSelectionMap = forwardRef<SynthSelectionMapHandle, SynthSelectionMapProps>(
  ({ onBoundsChange }, ref) => {
    const [map, setMap] = useState<L.Map | null>(null)
    const [drawnItems, setDrawnItems] = useState<FeatureGroup<any> | null>(null)
    const [drawControl, setDrawControl] = useState<L.Draw.Rectangle | null>(null)
    const mapInitializedRef = useRef<boolean>(false)
    const isDrawingRef = useRef<boolean>(false)
    const isEditingRef = useRef<boolean>(false)
    const startPointRef = useRef<L.LatLng | null>(null)
    const originalRectRef = useRef<L.Rectangle | null>(null)

    // Helper function to create wrapped copies
    const createWrappedCopies = (bounds: L.LatLngBounds, options: L.PathOptions) => {
      const sw = bounds.getSouthWest()
      const ne = bounds.getNorthEast()

      return [
        L.rectangle(
          L.latLngBounds(L.latLng(sw.lat, sw.lng + 360), L.latLng(ne.lat, ne.lng + 360)),
          options
        ),
        L.rectangle(
          L.latLngBounds(L.latLng(sw.lat, sw.lng - 360), L.latLng(ne.lat, ne.lng - 360)),
          options
        ),
      ]
    }

    // Helper function to update form coordinates
    const updateFormCoordinates = useCallback(
      (bounds: L.LatLngBounds) => {
        const ne = bounds.getNorthEast()
        const sw = bounds.getSouthWest()

        onBoundsChange({
          north: ne.lat.toString(),
          south: sw.lat.toString(),
          east: ((((ne.lng % 360) + 540) % 360) - 180).toString(),
          west: ((((sw.lng % 360) + 540) % 360) - 180).toString(),
        })
      },
      [onBoundsChange]
    )

    // Helper function to start edit mode
    const enableEditMode = () => {
      if (!isEditingRef.current) {
        // Find and click the edit button
        const editButton = document.querySelector('.leaflet-draw-edit-edit')
        if (editButton instanceof HTMLElement) {
          editButton.click()
        }
      }
    }

    // Add this new function
    const focusOnBounds = () => {
      if (originalRectRef.current) {
        const bounds = originalRectRef.current.getBounds()
        map?.fitBounds(bounds, { padding: [50, 50] })
      }
    }

    // Add this new function
    const setBoundsFromConfig = (bounds: {
      north: string
      south: string
      east: string
      west: string
    }) => {
      if (!drawnItems || !map) return

      // Clear existing layers
      drawnItems.clearLayers()

      // Create rectangle from bounds
      const north = parseFloat(bounds.north)
      const south = parseFloat(bounds.south)
      const east = parseFloat(bounds.east)
      const west = parseFloat(bounds.west)

      const rectangle = L.rectangle(
        [
          [north, west],
          [south, east],
        ],
        {
          color: '#3388ff',
          weight: 2,
          opacity: 0.5,
          fillOpacity: 0.2,
        }
      )

      // Store as original rectangle
      originalRectRef.current = rectangle
      drawnItems.addLayer(rectangle)

      // Create and add wrapped copies
      const wrappedCopies = createWrappedCopies(rectangle.getBounds(), rectangle.options)
      wrappedCopies.forEach((copy) => drawnItems.addLayer(copy))

      // Update form coordinates
      updateFormCoordinates(rectangle.getBounds())
    }

    // Expose methods to parent component
    useImperativeHandle(ref, () => ({
      clearBounds: () => {
        if (drawnItems) {
          drawnItems.clearLayers()
          originalRectRef.current = null
        }
        onBoundsChange(null)
      },
      startDrawing: () => {
        if (drawControl) {
          drawControl.enable()
        }
      },
      focusBounds: focusOnBounds,
      setBounds: setBoundsFromConfig,
    }))

    // Initialize map
    useEffect(() => {
      const mapContainer = document.getElementById('synth-map')

      if (!mapContainer || mapInitializedRef.current) {
        return
      }

      mapInitializedRef.current = true

      setTimeout(() => {
        try {
          const northSouthBounds: LatLngTuple[] = [
            [-85, -Infinity],
            [85, Infinity],
          ]

          // Initialize map with safer options
          const mapInstance = L.map('synth-map', {
            maxBounds: northSouthBounds,
            maxBoundsViscosity: 1.0,
            minZoom: 3,
            maxZoom: 18,
            zoomControl: false,
            zoomSnap: 0.5,
            wheelPxPerZoomLevel: 120,
            fadeAnimation: true,
            zoomAnimation: true,
            worldCopyJump: true,
            preferCanvas: true,
          })

          // Add zoom control to the top-left corner
          L.control
            .zoom({
              position: 'topleft',
            })
            .addTo(mapInstance)

          // Add scale control to the bottom-left corner
          L.control
            .scale({
              imperial: true,
              metric: true,
              position: 'topright',
            })
            .addTo(mapInstance)

          // Add base tile layer
          L.tileLayer(
            'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}',
            {
              attribution:
                '&copy; Esri &mdash; Source: Esri, i-cubed, USDA, USGS, AEX, GeoEye, Getmapping, Aerogrid, IGN, IGP, UPR-EGP, and the GIS User Community',
              maxZoom: 19,
              crossOrigin: true,
              noWrap: false,
            }
          ).addTo(mapInstance)

          // Create feature group for drawn items
          const drawnItemsLayer = new L.FeatureGroup()
          setDrawnItems(drawnItemsLayer)
          mapInstance.addLayer(drawnItemsLayer)

          // Set initial view
          setTimeout(() => {
            mapInstance.setView([39.8283, -98.5795], 3)
          }, 100)

          // Store map instance in state
          setMap(mapInstance)

          // Setup drawn item events
          mapInstance.on(L.Draw.Event.DRAWSTART, () => {
            if (drawnItemsLayer) {
              drawnItemsLayer.clearLayers()
              originalRectRef.current = null
            }
            isDrawingRef.current = true
            startPointRef.current = null
            onBoundsChange(null)
          })

          mapInstance.on('mousemove', (e: L.LeafletMouseEvent) => {
            if (isDrawingRef.current) {
              if (!startPointRef.current) {
                startPointRef.current = e.latlng
              } else {
                const bounds = L.latLngBounds(startPointRef.current, e.latlng)
                updateFormCoordinates(bounds)
              }
            }
          })

          mapInstance.on('mousedown', (e: L.LeafletMouseEvent) => {
            if (isDrawingRef.current) {
              startPointRef.current = e.latlng
            }
          })

          mapInstance.on(L.Draw.Event.DRAWSTOP, () => {
            isDrawingRef.current = false
            startPointRef.current = null
          })

          // Handle edit mode events
          mapInstance.on('draw:editstart', () => {
            isEditingRef.current = true
            // Hide wrapped copies during edit
            if (drawnItemsLayer) {
              drawnItemsLayer.eachLayer((layer) => {
                if (layer !== originalRectRef.current) {
                  drawnItemsLayer.removeLayer(layer)
                }
              })
            }
          })

          // Track all changes during edit with mousemove
          mapInstance.on('mousemove', () => {
            if (isEditingRef.current && originalRectRef.current) {
              const bounds = originalRectRef.current.getBounds()
              if (bounds) {
                updateFormCoordinates(bounds)
              }
            }
          })

          mapInstance.on('draw:editstop', () => {
            isEditingRef.current = false
            // Restore wrapped copies
            if (originalRectRef.current && drawnItemsLayer) {
              const bounds = originalRectRef.current.getBounds()
              const wrappedCopies = createWrappedCopies(bounds, originalRectRef.current.options)
              wrappedCopies.forEach((copy) => drawnItemsLayer.addLayer(copy))
            }
          })

          // Handle edit completion
          mapInstance.on('draw:edited', (e: any) => {
            const layers = e.layers
            layers.eachLayer((layer: L.Rectangle) => {
              if (drawnItemsLayer) {
                drawnItemsLayer.clearLayers()

                // Update the original rectangle reference
                originalRectRef.current = layer
                drawnItemsLayer.addLayer(layer)

                // Create and add new wrapped copies
                const wrappedCopies = createWrappedCopies(layer.getBounds(), layer.options)
                wrappedCopies.forEach((copy) => drawnItemsLayer.addLayer(copy))

                // Update form coordinates
                updateFormCoordinates(layer.getBounds())
              }
            })
          })

          mapInstance.on(L.Draw.Event.CREATED, (e: any) => {
            const layer = e.layer as L.Rectangle
            isDrawingRef.current = false
            startPointRef.current = null

            if (drawnItemsLayer) {
              drawnItemsLayer.clearLayers()

              // Store the original rectangle
              originalRectRef.current = layer
              drawnItemsLayer.addLayer(layer)

              // Create and add wrapped copies
              const wrappedCopies = createWrappedCopies(layer.getBounds(), layer.options)
              wrappedCopies.forEach((copy) => drawnItemsLayer.addLayer(copy))

              // Update form coordinates
              updateFormCoordinates(layer.getBounds())

              // Disable drawing mode after a rectangle is drawn
              if (drawControl) {
                drawControl.disable()
              }
            }
          })

          // Initialize draw control
          const newDrawControl = new (L.Draw as any).Rectangle(mapInstance, {
            shapeOptions: {
              color: '#3388ff',
              weight: 2,
              opacity: 0.5,
              fillOpacity: 0.2,
            },
          })
          setDrawControl(newDrawControl)

          // Initialize edit control with custom styles
          const editToolbar = new L.Control.Draw({
            draw: {
              rectangle: false,
              circle: false,
              circlemarker: false,
              marker: false,
              polyline: false,
              polygon: false,
            },
            edit: {
              featureGroup: drawnItemsLayer,
              remove: false,
              edit: {
                selectedPathOptions: {
                  color: '#3388ff',
                  fillColor: '#3388ff',
                  fillOpacity: 0.2,
                  weight: 2,
                  opacity: 0.5,
                },
              },
            },
          })
          mapInstance.addControl(editToolbar)

          // Add custom edit handle styles
          const EditRectangle = L.Edit.Rectangle as any
          if (EditRectangle && EditRectangle.prototype) {
            EditRectangle.prototype.options = EditRectangle.prototype.options || {}
            EditRectangle.prototype.options.moveIcon = new L.DivIcon({
              iconSize: new L.Point(12, 12),
              className: 'leaflet-div-icon leaflet-editing-icon leaflet-edit-move',
            })

            EditRectangle.prototype.options.resizeIcon = new L.DivIcon({
              iconSize: new L.Point(12, 12),
              className: 'leaflet-div-icon leaflet-editing-icon',
            })
          }

          // Add click handler for the map
          mapInstance.on('click', (e: L.LeafletMouseEvent) => {
            if (originalRectRef.current && !isDrawingRef.current && !isEditingRef.current) {
              const bounds = originalRectRef.current.getBounds()
              const point = e.latlng

              // Check if click is within the rectangle bounds
              if (bounds.contains(point)) {
                // Small delay to ensure click event doesn't interfere
                setTimeout(() => {
                  enableEditMode()
                }, 50)
              }
            }
          })
        } catch (error) {
          console.error('Error initializing map:', error)
          mapInitializedRef.current = false
        }
      }, 0)

      // Cleanup function
      return () => {
        if (map) {
          map.remove()
          setMap(null)
          mapInitializedRef.current = false
        }
      }
    }, [drawControl, map, onBoundsChange, updateFormCoordinates])

    // Fix Leaflet default icon path issue
    useEffect(() => {
      delete (L.Icon.Default.prototype as any)._getIconUrl
      L.Icon.Default.mergeOptions({
        iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
        iconUrl: require('leaflet/dist/images/marker-icon.png'),
        shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
      })
    }, [])

    return (
      <StyledMapContainer>
        <div id="synth-map" />
      </StyledMapContainer>
    )
  }
)

export default SynthSelectionMap
